import {
  ApiVersion,
  AuthToken,
  TokenPayload,
  AuthError,
  TokenExpiredError,
  DynamicConfiguration,
  EmailObjectSummary,
  CreateEmailObjectRequest,
} from '../types';
import { HttpError } from '../types/ClientTypes';
import { RawEmailData, StakeholderCreationRequest, StakeholderInformation } from '../types/CreateEmailObjectRequest';

interface RequestOptions extends RequestInit {
  requiresAuth?: boolean;
}

export class CMClient {
  private clientUrl: string;
  private authToken: AuthToken | null = null;
  private readonly API_VERSION: ApiVersion;

  constructor(clientUrl: string, version: ApiVersion = '1.0') {
    this.clientUrl = clientUrl;
    this.API_VERSION = version;
  }

  private parseJwt(token: string): TokenPayload {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      throw new AuthError('Invalid token format');
    }
  }

  private isTokenExpired(token: AuthToken): boolean {
    return token.expiresAt <= Date.now();
  }

  private async request<T>(endpoint: string, options: RequestOptions = { method: 'GET', requiresAuth: true }): Promise<T> {
    const { requiresAuth = true, ...fetchOptions } = options;

    if (requiresAuth) {
      if (!this.authToken) {
        throw new AuthError('No authentication token present');
      }

      if (this.isTokenExpired(this.authToken)) {
        throw new TokenExpiredError();
      }
    }

    const requestUrl = new URL(`outlook/${endpoint}`, this.clientUrl);
    requestUrl.searchParams.append('api-version', this.API_VERSION);

    const headers = {
      'Content-Type': 'application/json',
      ...(requiresAuth && this.authToken ? { Authorization: `Bearer ${this.authToken.token}` } : {}),
      ...fetchOptions.headers,
    };

    const response = await fetch(requestUrl, {
      ...fetchOptions,
      headers,
    });

    if (response.status === 401) {
      this.authToken = null;
      throw new AuthError('Unauthorized request');
    }

    if (!response.ok) {
      let data = response.statusText;
      try {
        data = await response.json();
      } catch (e) {
        // Ignore error
      }
      throw new HttpError(response.status, data);
    }

    return response.json();
  }

  public async setAuthToken(token: string): Promise<void> {
    try {
      const payload = this.parseJwt(token);
      if (
        this.isTokenExpired({
          token,
          expiresAt: payload.exp * 1000,
        })
      ) {
        throw new TokenExpiredError();
      }
      this.authToken = {
        token,
        expiresAt: payload.exp * 1000,
      };
    } catch (error) {
      this.authToken = null;
      throw error;
    }
  }

  public async fetchConfiguration(): Promise<DynamicConfiguration> {
    return this.request<DynamicConfiguration>('');
  }

  public async createEmail(emailData: CreateEmailObjectRequest): Promise<EmailObjectSummary> {
    return this.request<EmailObjectSummary>('', { method: 'POST', body: JSON.stringify(emailData) });
  }

  public async findExistingStakeholders(stakeholderEmails: string[]): Promise<StakeholderInformation[]> {
    return this.request<StakeholderInformation[]>('stakeholders/search', {
      method: 'POST',
      body: JSON.stringify(stakeholderEmails),
    });
  }

  // Primarily used for finding emails that are on users, so we can filter them out from the unknown stakeholders section.
  public async findExistingUsers(emails: string[]): Promise<StakeholderInformation[]> {
    return this.request<StakeholderInformation[]>('users/search', {
      method: 'POST',
      body: JSON.stringify(emails),
    });
  }

  public async createStakeholder(stakeholderData: StakeholderCreationRequest): Promise<StakeholderInformation> {
    return this.request<StakeholderInformation>('stakeholders', {
      method: 'POST',
      body: JSON.stringify(stakeholderData),
    });
  }

  public async getSummary(summaryRequest: RawEmailData): Promise<EmailObjectSummary> {
    return this.request<EmailObjectSummary>('summary', {
      method: 'POST',
      body: JSON.stringify(summaryRequest),
    });
  }

  public async isEnabled(): Promise<boolean> {
    try {
      await this.request<boolean>('enabled', {
        method: 'GET',
        requiresAuth: false,
      });
      return true;
    } catch (e) {
      return false;
    }
  }

  public get isAuthenticated(): boolean {
    return !!this.authToken && !this.isTokenExpired(this.authToken);
  }

  public clearAuth(): void {
    this.authToken = null;
  }
}
